import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  Plus, Edit, Trash2, Send, Search, Filter, RefreshCw, Zap, Eye, Copy, Download,
  X, Save, FileText, MessageSquare, Settings, Palette, Sparkles, Globe, Tag,
  ChevronLeft, ChevronRight, MoreVertical, CheckCircle, AlertCircle, Clock
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { whatsappTemplatesAPI } from '../../services/api';
import TestInput from './TestInput';
import StableInput from './StableInput';
import IsolatedForm from './IsolatedForm';
import IsolatedAIGenerator from './IsolatedAIGenerator';

const WhatsAppTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showIsolatedForm, setShowIsolatedForm] = useState(false);
  const [showIsolatedAI, setShowIsolatedAI] = useState(false);
  const [filters, setFilters] = useState({ 
    status: '', 
    category: '', 
    language: '', 
    search: '' 
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Form state - using individual state variables to avoid object mutation issues
  const [templateName, setTemplateName] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [templateLanguage, setTemplateLanguage] = useState('en');
  const [templateContent, setTemplateContent] = useState('');
  const [templateVariables, setTemplateVariables] = useState([]);
  const [isActive, setIsActive] = useState(1);
  
  // Stable input components handle focus internally

  // Computed formData for API calls
  const formData = {
    template_name: templateName,
    template_category: templateCategory,
    template_language: templateLanguage,
    template_content: templateContent,
    template_variables: templateVariables,
    is_active: isActive
  };

  // AI Generator state
  const [aiFormData, setAiFormData] = useState({
    prompt: '',
    industry: '',
    language: 'en'
  });
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGeneratedData, setAiGeneratedData] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      const response = await whatsappTemplatesAPI.getTemplates(params);
      if (response.data && response.data.success) {
        setTemplates(response.data.data.templates);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  // Separate function for manual refresh (without dependencies)
  const refreshTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      };
      
      const response = await whatsappTemplatesAPI.getTemplates(params);
      if (response.data && response.data.success) {
        setTemplates(response.data.data.templates);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const response = await whatsappTemplatesAPI.getCategories();
      
      if (response && response.data && response.data.success) {
        setCategories(response.data.data);
      } else {
        console.error('Categories API error:', response?.data?.error || response?.data?.message || 'Unknown error');
        toast.error('Failed to load categories');
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load categories');
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);



  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (editingTemplate) {
        const response = await whatsappTemplatesAPI.updateTemplate(editingTemplate.id, formData);
        if (response.data && response.data.success) {
          toast.success('Template updated successfully');
          setShowForm(false);
          setEditingTemplate(null);
          resetForm();
          refreshTemplates();
        }
      } else {
        const response = await whatsappTemplatesAPI.createTemplate(formData);
        if (response.data && response.data.success) {
          toast.success('Template created successfully');
          setShowForm(false);
          resetForm();
          refreshTemplates();
        }
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setShowIsolatedForm(true);
  };

  const handleAddNew = () => {
    setEditingTemplate(null);
    setShowIsolatedForm(true);
  };

  const handleIsolatedFormSuccess = () => {
    refreshTemplates();
  };

  const handleIsolatedFormClose = () => {
    setShowIsolatedForm(false);
    setEditingTemplate(null);
    setAiGeneratedData(null);
  };

  const handleAIGeneratorOpen = () => {
    setShowIsolatedAI(true);
  };

  const handleAIGeneratorClose = () => {
    setShowIsolatedAI(false);
  };

  const handleAITemplateGenerated = (aiResult) => {
    // Store the AI-generated data and show the form
    setAiGeneratedData(aiResult);
    setShowIsolatedForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        const response = await whatsappTemplatesAPI.deleteTemplate(id);
        if (response.data && response.data.success) {
          toast.success('Template deleted successfully');
          refreshTemplates();
        }
      } catch (error) {
        console.error('Error deleting template:', error);
        toast.error('Failed to delete template');
      }
    }
  };

  const handleSubmitToMeta = async (id) => {
    try {
      const response = await whatsappTemplatesAPI.submitTemplate(id);
      if (response.data && response.data.success) {
        toast.success('Template submitted to Meta for approval');
        refreshTemplates();
      }
    } catch (error) {
      console.error('Error submitting template:', error);
      toast.error('Failed to submit template to Meta');
    }
  };

  const handleLoadFromMeta = async () => {
    try {
      setLoading(true);
      console.log('🔄 Loading templates from Meta WhatsApp API...');
      
      const response = await whatsappTemplatesAPI.loadFromMeta();
      
      if (response.data && response.data.success) {
        const { loaded, updated, errors } = response.data.data;
        
        if (loaded > 0 || updated > 0) {
          toast.success(`Successfully loaded ${loaded} new templates and updated ${updated} existing templates`);
          refreshTemplates(); // Refresh the templates list
        } else {
          toast('No new templates found in Meta WhatsApp API', { icon: 'ℹ️' });
        }
        
        if (errors && errors.length > 0) {
          console.warn('⚠️ Some templates had errors:', errors);
          toast(`${errors.length} templates had errors during loading`, { icon: '⚠️' });
        }
      } else {
        toast.error(response.data?.message || 'Failed to load templates from Meta');
      }
    } catch (error) {
      console.error('Error loading templates from Meta:', error);
      toast.error('Failed to load templates from Meta WhatsApp API');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMetaManagement = async () => {
    try {
      console.log('🔗 Getting Meta WhatsApp Template Management URL...');
      
      const response = await whatsappTemplatesAPI.getMetaManagementUrl();
      
      if (response.data && response.data.success) {
        const { url } = response.data.data;
        console.log('🔗 Opening Meta management URL:', url);
        
        // Open in new tab
        window.open(url, '_blank', 'noopener,noreferrer');
        toast.success('Opening Meta WhatsApp Template Management in new tab');
      } else {
        toast.error(response.data?.message || 'Failed to get Meta management URL');
      }
    } catch (error) {
      console.error('Error getting Meta management URL:', error);
      toast.error('Failed to open Meta WhatsApp Template Management');
    }
  };

  const resetForm = () => {
    setTemplateName('');
    setTemplateCategory('');
    setTemplateLanguage('en');
    setTemplateContent('');
    setTemplateVariables([]);
    setIsActive(1);
    setEditingTemplate(null);
  };

  const generateAITemplate = async () => {
    try {
      setAiLoading(true);
      const response = await whatsappTemplatesAPI.generateAITemplate(aiFormData);
      if (response.data && response.data.success) {
        setAiResult(response.data.data);
        toast.success('AI template generated successfully');
      } else {
        toast.error(response.data?.error || 'Failed to generate AI template');
      }
    } catch (error) {
      console.error('Error generating AI template:', error);
      toast.error('Failed to generate AI template');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAITemplate = (aiTemplate) => {
    setTemplateName(aiTemplate.template_name || 'AI Generated Template');
    setTemplateCategory(aiTemplate.category || 'Marketing');
    setTemplateLanguage(aiTemplate.language || 'en');
    setTemplateContent(aiTemplate.body_text || aiTemplate.template_content || '');
    setTemplateVariables(aiTemplate.variables || []);
    setIsActive(1);
    setShowAIGenerator(false);
    setShowForm(true);
    toast.success('AI template applied to form');
  };

  const addVariable = () => {
    const newVar = prompt('Enter variable name (e.g., {name}):');
    if (newVar && !templateVariables.includes(newVar)) {
      setTemplateVariables([...templateVariables, newVar]);
    }
  };

  const removeVariable = (index) => {
    setTemplateVariables(templateVariables.filter((_, i) => i !== index));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'rejected': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'in_review': return <Clock className="w-4 h-4 text-blue-500" />;
      case 'disabled': return <AlertCircle className="w-4 h-4 text-gray-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (template) => {
    const metaStatus = template.meta_status || template.status;
    const localStatus = template.status;
    
    // Priority: Meta status > Local status
    const displayStatus = (metaStatus || localStatus)?.toLowerCase();
    
    const statusConfig = {
      'approved': { 
        color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
        label: 'Approved',
        icon: <CheckCircle className="w-3 h-3" />
      },
      'pending': { 
        color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
        label: 'Pending',
        icon: <Clock className="w-3 h-3" />
      },
      'in_review': { 
        color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
        label: 'In Review',
        icon: <Clock className="w-3 h-3" />
      },
      'rejected': { 
        color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
        label: 'Rejected',
        icon: <AlertCircle className="w-3 h-3" />
      },
      'disabled': { 
        color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
        label: 'Disabled',
        icon: <AlertCircle className="w-3 h-3" />
      },
      'draft': { 
        color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300',
        label: 'Draft',
        icon: <Clock className="w-3 h-3" />
      }
    };
    
    const config = statusConfig[displayStatus] || statusConfig['draft'];
    
    return (
      <div className="flex items-center space-x-1">
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
          {config.icon}
          <span className="ml-1">{config.label}</span>
        </span>
        {metaStatus && localStatus && metaStatus !== localStatus && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            (Local: {localStatus})
          </span>
        )}
      </div>
    );
  };

  const getCategoryColor = (category) => {
    const colors = {
      'marketing': 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300',
      'support': 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
      'notification': 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
      'welcome': 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
      'reminder': 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300',
      'survey': 'bg-pink-100 dark:bg-pink-900/30 text-pink-800 dark:text-pink-300',
      'appointment': 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300',
      'order': 'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300',
      'utility': 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300'
    };
    return colors[category?.toLowerCase()] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
  };

  // Portal Edit Form Component
  const EditPortal = () => {
    if (!showForm) return null;

    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <FileText className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold">
                    {editingTemplate ? 'Edit Template' : 'Create New Template'}
                  </h2>
                  <p className="text-blue-100">
                    {editingTemplate ? 'Update your WhatsApp template' : 'Create a new WhatsApp message template'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  // Check if there's content in the form
                  if (templateName.trim() || templateContent.trim()) {
                    if (window.confirm('You have unsaved changes. Are you sure you want to close the form?')) {
                      setShowForm(false);
                      setEditingTemplate(null);
                      resetForm();
                    }
                  } else {
                    setShowForm(false);
                    setEditingTemplate(null);
                    resetForm();
                  }
                }}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                title="Close form"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <TestInput />
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Template Name *
                  </label>
                  <StableInput
                    value={templateName}
                    onChange={(e) => {
                      console.log('Template name input changed to:', e.target.value);
                      setTemplateName(e.target.value);
                    }}
                    placeholder="Enter template name"
                    required
                    autoComplete="off"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500 dark:placeholder-gray-400 shadow-sm dark:shadow-md"
                  />
                  <p style={{color: 'red', fontSize: '12px'}}>Debug: {templateName}</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Category *
                  </label>
                  <select
                    value={templateCategory}
                    onChange={(e) => setTemplateCategory(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.length > 0 ? categories.map(category => (
                      <option key={category.id} value={category.name.toLowerCase()}>
                        {category.name}
                      </option>
                    )) : (
                      <option value="" disabled>Loading categories...</option>
                    )}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:p-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Language
                  </label>
                  <select
                    value={templateLanguage}
                    onChange={(e) => setTemplateLanguage(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                    <option value="te">Telugu</option>
                    <option value="ta">Tamil</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Status
                  </label>
                  <select
                    value={isActive}
                    onChange={(e) => setIsActive(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              {/* Template Content */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Template Content *
                </label>
                <StableInput
                  type="textarea"
                  value={templateContent}
                  onChange={(e) => {
                    console.log('Template content input changed to:', e.target.value);
                    setTemplateContent(e.target.value);
                  }}
                  placeholder="Enter your message template content. Use {{variableName}} for dynamic content."
                  required
                  autoComplete="off"
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500 dark:placeholder-gray-400 resize-none shadow-sm dark:shadow-md"
                />
                <p style={{color: 'red', fontSize: '12px'}}>Debug: {templateContent}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Use double curly braces for variables: {`{{name}}, {{orderNumber}}, etc.`}
                </p>
              </div>

              {/* Variables */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Template Variables
                  </label>
                  <button
                    type="button"
                    onClick={addVariable}
                    className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors text-sm font-medium"
                  >
                    <Plus className="w-4 h-4 inline mr-1" />
                    Add Variable
                  </button>
                </div>
                <div className="space-y-2">
                  {templateVariables.map((variable, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <span className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-mono">
                        {variable}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeVariable(index)}
                        className="p-1 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {templateVariables.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No variables added yet</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-4 pt-6 border-t dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    // Check if there's content in the form
                    if (templateName.trim() || templateContent.trim()) {
                      if (window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
                        setShowForm(false);
                        setEditingTemplate(null);
                        resetForm();
                      }
                    } else {
                      setShowForm(false);
                      setEditingTemplate(null);
                      resetForm();
                    }
                  }}
                  className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-medium flex items-center space-x-2 disabled:opacity-50"
                >
                  {loading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  <span>{editingTemplate ? 'Update Template' : 'Create Template'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // AI Generator Portal
  const AIGeneratorPortal = () => {
    if (!showAIGenerator) return null;

    return createPortal(
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold">AI Template Generator</h2>
                  <p className="text-purple-100">Generate templates using AI</p>
                </div>
              </div>
              <button
                onClick={() => setShowAIGenerator(false)}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Describe your template
              </label>
              <StableInput
                type="textarea"
                value={aiFormData.prompt}
                onChange={(e) => setAiFormData(prev => ({...prev, prompt: e.target.value}))}
                placeholder="e.g., Welcome message for new customers with discount offer"
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all placeholder-gray-500 dark:placeholder-gray-400 shadow-sm dark:shadow-md"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Industry
                </label>
                <select
                  value={aiFormData.industry}
                  onChange={(e) => setAiFormData(prev => ({...prev, industry: e.target.value}))}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <option value="">Select Industry</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="education">Education</option>
                  <option value="finance">Finance</option>
                  <option value="technology">Technology</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Language
                </label>
                <select
                  value={aiFormData.language}
                  onChange={(e) => setAiFormData(prev => ({...prev, language: e.target.value}))}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                >
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                  <option value="te">Telugu</option>
                </select>
              </div>
            </div>

            <button
              onClick={generateAITemplate}
              disabled={aiLoading || !aiFormData.prompt}
              className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all font-medium flex items-center justify-center space-x-2 disabled:opacity-50"
            >
              {aiLoading ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              <span>Generate Template</span>
            </button>

            {aiResult && (
              <div className="border border-purple-200 dark:border-purple-700 rounded-xl p-4 bg-purple-50 dark:bg-purple-900/20">
                <h4 className="font-semibold text-purple-800 dark:text-purple-300 mb-3">Generated Template:</h4>
                <div className="space-y-3">
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{aiResult.template_name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Category:</span>
                    <span className="ml-2 text-gray-600 dark:text-gray-400">{aiResult.category}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">Content:</span>
                    <p className="mt-1 p-3 bg-white dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 border dark:border-gray-600">
                      {aiResult.body_text || aiResult.template_content}
                    </p>
                  </div>
                  {aiResult.variables && aiResult.variables.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Variables:</span>
                      <div className="mt-1 flex flex-wrap gap-2">
                        {aiResult.variables.map((variable, index) => (
                          <span key={index} className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-sm">
                            {variable}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => applyAITemplate(aiResult)}
                  className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Use This Template
                </button>
              </div>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="p-2 sm:p-3 bg-gradient-to-r from-green-500 to-blue-500 rounded-xl">
                <MessageSquare className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">WhatsApp Templates</h1>
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1">Manage your WhatsApp message templates</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <button
                onClick={handleLoadFromMeta}
                className="px-3 sm:px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all flex items-center justify-center space-x-2 shadow-lg text-sm sm:text-base"
                disabled={loading}
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">Load Templates</span>
                <span className="sm:hidden">Load</span>
              </button>
              <button
                onClick={handleOpenMetaManagement}
                className="px-3 sm:px-4 py-2 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all flex items-center justify-center space-x-2 shadow-lg text-sm sm:text-base"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Template Management</span>
                <span className="sm:hidden">Templates</span>
              </button>
              <button
                onClick={handleAIGeneratorOpen}
                className="px-3 sm:px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all flex items-center justify-center space-x-2 shadow-lg text-sm sm:text-base"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">AI Generator</span>
                <span className="sm:hidden">AI</span>
              </button>
              <button
                onClick={handleAddNew}
                className="px-3 sm:px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all flex items-center justify-center space-x-2 shadow-lg text-sm sm:text-base"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">New Template</span>
                <span className="sm:hidden">New</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search templates..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>
          </div>
          <select
            value={filters.category}
            onChange={(e) => setFilters({...filters, category: e.target.value})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.name.toLowerCase()}>
                {category.name}
              </option>
            ))}
          </select>
          <select
            value={filters.language}
            onChange={(e) => setFilters({...filters, language: e.target.value})}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            <option value="">All Languages</option>
            <option value="en">English</option>
            <option value="en_US">English US</option>
            <option value="hi">Hindi</option>
            <option value="te">Telugu</option>
          </select>
          <button
            onClick={loadTemplates}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Loading templates...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:p-6 mb-8">
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-sm border dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Templates</p>
                    <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{pagination.total}</p>
                  </div>
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                    <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-sm border dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600 dark:text-green-400">
                      {templates.filter(t => t.is_active === 1).length}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-sm border dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Categories</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-600 dark:text-purple-400">{categories.length}</p>
                  </div>
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                    <Tag className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-6 shadow-sm border dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Languages</p>
                    <p className="text-xl sm:text-2xl font-bold text-orange-600 dark:text-orange-400">4</p>
                  </div>
                  <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
                    <Globe className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:p-6">
              {templates.map((template) => (
                <div key={template.id} className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border dark:border-gray-700 hover:shadow-lg transition-all duration-300 overflow-hidden">
                  <div className="p-4 sm:p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                          {template.template_name}
                        </h3>
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(template.template_category)}`}>
                            {template.category_name || template.template_category}
                          </span>
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full text-xs font-medium">
                            {template.template_language.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(template)}
                        <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                          <MoreVertical className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        </button>
                      </div>
                    </div>

                    {/* Template Features */}
                    <div className="mb-4 space-y-2">
                      {/* Header */}
                      {template.header_text && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Header:</span>
                          <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded">
                            {template.header_type === 'TEXT' ? template.header_text : `${template.header_type} Header`}
                          </span>
                        </div>
                      )}
                      
                      {/* Footer */}
                      {template.footer_text && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Footer:</span>
                          <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
                            {template.footer_text}
                          </span>
                        </div>
                      )}
                      
                      {/* Buttons */}
                      {template.button_type && template.button_text && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Button:</span>
                          <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                            {template.button_text} ({template.button_type})
                          </span>
                        </div>
                      )}
                      
                      {/* Media */}
                      {template.media_type && template.media_url && (
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Media:</span>
                          <span className="text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded">
                            {template.media_type}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="mb-4">
                      <p className="text-gray-600 dark:text-gray-300 text-sm line-clamp-3">
                        {template.template_content}
                      </p>
                    </div>

                    {template.template_variables && template.template_variables !== 'null' && (() => {
                      try {
                        const variables = JSON.parse(template.template_variables);
                        if (Array.isArray(variables) && variables.length > 0) {
                          return (
                            <div className="mb-4">
                              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Variables:</p>
                              <div className="flex flex-wrap gap-1">
                                {variables.map((variable, index) => (
                                  <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-mono">
                                    {variable}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        }
                      } catch (error) {
                        console.error('Error parsing template variables:', error);
                      }
                      return null;
                    })()}

                    <div className="flex items-center justify-between pt-4 border-t dark:border-gray-700">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => copyToClipboard(template.template_content)}
                          className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Copy content"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(template)}
                          className="p-2 text-blue-400 dark:text-blue-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Edit template"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleSubmitToMeta(template.id)}
                          className="p-2 text-green-400 dark:text-green-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors"
                          title="Submit to Meta"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="p-2 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Delete template"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        template.is_active === 1 
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {template.is_active === 1 ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex items-center justify-center space-x-2 mt-8">
                <button
                  onClick={() => setPagination({...pagination, page: pagination.page - 1})}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination({...pagination, page: pagination.page + 1})}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Portals */}
      <EditPortal />
      <AIGeneratorPortal />
      
      {/* Isolated Form */}
      <IsolatedForm
        isOpen={showIsolatedForm}
        onClose={handleIsolatedFormClose}
        editingTemplate={editingTemplate}
        categories={categories}
        onSuccess={handleIsolatedFormSuccess}
        aiGeneratedData={aiGeneratedData}
      />
      
      {/* Isolated AI Generator */}
      <IsolatedAIGenerator
        isOpen={showIsolatedAI}
        onClose={handleAIGeneratorClose}
        onTemplateGenerated={handleAITemplateGenerated}
      />
    </div>
  );
};

export default WhatsAppTemplates;